import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageContainer,
} from "@repo/ui";
import { useAuthToken } from "@convex-dev/auth/react";
import { useQuery } from "convex/react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router";
import { api } from "@convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "@convex/schema";
import { PYRAMIDE_FIT_CULTUREL } from "@convex/referentiel/qsort";
import {
  AutodiagResults,
  Copsoq3Results,
  EgogrammeResults,
  EntStructResults,
  FitCulturelResults,
  InbasketResults,
  PamiResultsEcharts,
  RapportsMenu,
} from "@repo/results";
import { versionCopsoq3, versionCopsoq3DeDonnees } from "@repo/utils";
import referentielEnedis from "../../../../referentiel_enedis.json";
import type { Id } from "@convex/_generated/dataModel";

const CONVEX_URL = process.env.BUN_PUBLIC_CONVEX_URL || "http://127.0.0.1:3210";

export function EvaluationResults() {
  const token = useAuthToken();
  const { evaluationId } = useParams<{
    evaluationId: string;
  }>();
  const [searchParams] = useSearchParams();
  const fromPath = searchParams.get("from");
  // Le seul appelant (`ParcoursDetailAdmin`) fournit toujours `?from=`, donc le
  // retour est exact. Le repli ne sert qu'à une URL collée à la main : il vise
  // la racine, la liste globale des parcours n'existant plus.
  const backLink = fromPath || "/";
  const backLabel = fromPath
    ? "Retour au parcours"
    : "Retour aux organisations";

  const result = useQuery(
    api.evaluations.obtenirEvaluationSuperAdmin,
    evaluationId ? { evaluationId: evaluationId as Id<"evaluations"> } : "skip",
  );

  // Rapports proposés pour cette évaluation (candidat, recruteur…) : liste
  // vide pour un type qui n'en a pas, et le menu ne s'affiche alors pas.
  const rapportsDisponibles = useQuery(
    api.reports.rapportsDisponibles,
    evaluationId ? { evaluationId: evaluationId as Id<"evaluations"> } : "skip",
  );

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (result === null) {
    return (
      <PageContainer>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">
            Évaluation introuvable
          </h1>
          <p className="mt-2 text-muted-foreground">
            L'évaluation demandée n'existe pas.
          </p>
          <Link to={backLink} className="mt-4 inline-block">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {backLabel}
            </Button>
          </Link>
        </div>
      </PageContainer>
    );
  }

  const { evaluation, tempsPasse } = result;

  const candidatInfo = {
    prenom: evaluation.prenomCandidat,
    nom: evaluation.nomCandidat,
    email: evaluation.emailCandidat,
    dateDebut: evaluation.dateDebut,
    dateFin: evaluation.dateFin,
    tempsPasse,
  };

  const menuRapports = (
    <RapportsMenu
      evaluationId={evaluation._id}
      rapports={rapportsDisponibles}
      candidatInfo={candidatInfo}
      token={token}
      convexUrl={CONVEX_URL}
    />
  );

  const renderResults = () => {
    const { donnees } = evaluation;

    if (donnees.type === TypeEvalEnum.INBASKET) {
      return (
        <InbasketResults
          donnees={donnees}
          candidatInfo={candidatInfo}
          resultatsRacine={evaluation.resultats}
        />
      );
    }

    if (donnees.type === TypeEvalEnum.FORM) {
      // donneesFormValidator est un objet unique, PAS une union d'objets : TypeScript
      // ne peut donc affiner ni donnees.template ni donnees.reponses vers les types
      // propres à un template par simple test d'égalité. D'où le cast vers unknown,
      // puis vers la forme de prop attendue.
      if (donnees.template === TemplateFormEnum.AUTODIAG) {
        type AutodiagShape = {
          type: "form";
          template: "autodiag";
          reponses: Record<
            string,
            {
              name: string;
              label: string;
              reponse: string;
              titre?: string;
              sous_questions?: {
                name: string;
                label: string;
                reponse: string;
              }[];
            }
          >;
        };
        return (
          <AutodiagResults
            donnees={donnees as unknown as AutodiagShape}
            referentiel={referentielEnedis}
            candidatInfo={candidatInfo}
          />
        );
      }
      if (donnees.template === TemplateFormEnum.EGOGRAMME) {
        type EgogrammeShape = {
          type: "form";
          template: "egogramme";
          reponses: Record<
            string,
            { name: string; label: string; profil: string; response?: boolean }
          >;
        };
        return (
          <EgogrammeResults
            donnees={donnees as unknown as EgogrammeShape}
            candidatInfo={candidatInfo}
          />
        );
      }
      if (donnees.template === TemplateFormEnum.COPSOQ3) {
        type ReponseNonScoree = {
          name: string;
          rubrique?: string;
          question: string;
          reponse: string | number | string[];
        };
        type Copsoq3Shape = {
          type: "form";
          template: "copsoq3";
          reponses: Record<
            string,
            {
              name: string;
              question: string;
              echelle: string;
              domaine: string;
              reponse: string;
              score: number;
            }
          >;
          profil?: ReponseNonScoree[];
          sante?: ReponseNonScoree[];
        };
        return (
          <Copsoq3Results
            donnees={donnees as unknown as Copsoq3Shape}
            candidatInfo={candidatInfo}
            version={versionCopsoq3(versionCopsoq3DeDonnees(donnees)).libelle}
          />
        );
      }
      if (donnees.template === TemplateFormEnum.FIT_CULTUREL) {
        type FitCulturelShape = {
          type: "form";
          template: "pc-fit";
          reponses: Record<
            string,
            { name: string; label: string; niveau: number; score?: number }
          >;
        };
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              {menuRapports}
            </div>
            <FitCulturelResults
              donnees={donnees as unknown as FitCulturelShape}
              candidatInfo={candidatInfo}
              nbNiveaux={PYRAMIDE_FIT_CULTUREL.length}
            />
          </div>
        );
      }
      if (donnees.template === TemplateFormEnum.PAMI) {
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              {menuRapports}
            </div>
            <PamiResultsEcharts
              donnees={donnees}
              candidatInfo={candidatInfo}
              resultatsRacine={evaluation.resultats}
            />
          </div>
        );
      }
      return <div>Type de formulaire inconnu</div>;
    }

    if (donnees.type === TypeEvalEnum.ENT_STRUCT) {
      return (
        <div className="space-y-4">
          <div className="flex justify-end">
            {menuRapports}
          </div>
          <EntStructResults
            donnees={donnees}
            candidatInfo={candidatInfo}
            resultatsRacine={evaluation.resultats}
            notationsParModele={evaluation.notationsParModele}
          />
        </div>
      );
    }

    return <div>Type d'évaluation non supporté</div>;
  };

  return (
    <PageContainer style={{ scrollbarGutter: "stable" }}>
      {/* Header */}
      <div className="mb-6">
        <Link
          to={backLink}
          className="text-sm text-muted-foreground hover:underline"
        >
          <ArrowLeft className="h-4 w-4 inline mr-1" />
          {backLabel}
        </Link>
      </div>

      {renderResults()}

      {/* Retours d'expérience */}
      {evaluation.retoursExperience && (
        <Card>
          <CardHeader>
            <CardTitle>Retours d'expérience</CardTitle>
            <CardDescription>
              Commentaires du candidat sur l'évaluation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(evaluation.retoursExperience).map(
                ([key, value]) => (
                  <div key={key} className="p-4 bg-muted rounded-surface">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      {key}
                    </p>
                    <p className="whitespace-pre-wrap">{value}</p>
                  </div>
                ),
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
